import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SortDescriptor } from '@progress/kendo-data-query';
import { GridPageChangeEvent } from '@progress/kendo-react-grid';
import { buildDocumentEndpointPath } from '@cs-cms/api-client';
import {
  useCurrentUserContext,
  useGetView,
  useGetViews,
  useGlobalizationContext,
  useSearch,
  useTranslation
} from '@cs-cms/state-management';
import {
  CONTENT_TYPE_FIELD,
  CpsFieldForView,
  CpsSearchQueryRequest,
  DEFAULT_ROWS_PER_PAGE,
  DOC_ID_FIELD,
  EXTENSION_FIELD
} from '@cs-cms/types';
import { exceedsMaxSearchSkip, useUIConfig } from '@cs-cms/ui';
import { buildSapInvoiceFilters } from './buildSapInvoiceFilters';
import { parseSapInvoiceSearchParams } from './parseSapInvoiceSearchParams';

export interface SapInvoiceGridDocument {
  [DOC_ID_FIELD]: string;
  [CONTENT_TYPE_FIELD]: string;
  [EXTENSION_FIELD]: string;
  [key: string]: unknown;
}

interface UseSapInvoiceDocumentsResult {
  isAccessDenied: boolean;
  isLoading: boolean;
  columns: CpsFieldForView[] | undefined;
  gridData: SapInvoiceGridDocument[];
  totalCount: number;
  skip: number;
  pageSize: number;
  sort: SortDescriptor[] | undefined;
  openDocumentLoading: boolean;
  translate: (key: string) => string;
  onPageChange: (event: GridPageChangeEvent) => void;
  onSortChange: (descriptors: SortDescriptor[]) => void;
  openDocument: (contentType: string, documentId: string) => void;
}

const buildSelectColumns = (columns: CpsFieldForView[]): string[] =>
  columns
    .map(column => column.name)
    .concat([DOC_ID_FIELD, CONTENT_TYPE_FIELD, EXTENSION_FIELD])
    .filter((value, index, array) => array.indexOf(value) === index);

export const useSapInvoiceDocuments = (): UseSapInvoiceDocumentsResult => {
  const [searchParams] = useSearchParams();
  const { apiUrl } = useUIConfig();
  const { currentUser, isLoading: isCurrentUserLoading } = useCurrentUserContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const [skip, setSkip] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sort, setSort] = useState<SortDescriptor[] | undefined>(undefined);
  const [openDocumentLoading, setOpenDocumentLoading] = useState(false);

  const parsedParams = useMemo(() => parseSapInvoiceSearchParams(searchParams), [searchParams]);

  const filters = useMemo(() => {
    if (!parsedParams) {
      return null;
    }
    return buildSapInvoiceFilters(parsedParams.filters);
  }, [parsedParams]);

  const indexId = parsedParams?.indexId;
  const hasIndexAccess =
    indexId != null && Boolean(currentUser?.indexes?.some(index => index.id === indexId));

  const isAccessDenied =
    !isCurrentUserLoading && (parsedParams == null || filters == null || !hasIndexAccess);

  const { data: views, isLoading: isViewsLoading } = useGetViews(hasIndexAccess ? indexId : undefined);

  const defaultViewId = useMemo(() => {
    if (!views?.length) {
      return undefined;
    }
    const defaultView = views.find(view => view.isDefault);
    return defaultView ? defaultView.id : views[0].id;
  }, [views]);

  const { data: currentView, isLoading: isViewLoading } = useGetView(defaultViewId, selectedLocale);
  const columns = currentView?.fields;

  const queryRequest = useMemo((): CpsSearchQueryRequest | undefined => {
    if (!hasIndexAccess || indexId == null || defaultViewId == null || !columns || filters == null) {
      return undefined;
    }

    return {
      size: pageSize,
      includeTotalCount: true,
      filters,
      select: buildSelectColumns(columns),
      indexId,
      viewId: defaultViewId,
      skip,
      orderBy: sort?.[0]?.field,
      orderByIsDescending: sort?.[0]?.dir === 'desc' ? true : undefined
    };
  }, [hasIndexAccess, indexId, defaultViewId, columns, filters, pageSize, skip, sort]);

  const { data, isLoading: isSearchLoading } = useSearch(queryRequest);

  const gridData = useMemo((): SapInvoiceGridDocument[] => {
    if (!data?.documentsWithPerms) {
      return [];
    }

    return data.documentsWithPerms.map(item => ({
      [DOC_ID_FIELD]: item.document[DOC_ID_FIELD] as string,
      [CONTENT_TYPE_FIELD]: item.document[CONTENT_TYPE_FIELD] as string,
      [EXTENSION_FIELD]: item.document[EXTENSION_FIELD] as string,
      ...item.document
    }));
  }, [data?.documentsWithPerms]);

  const openDocument = useCallback(
    (contentType: string, documentId: string): void => {
      if (indexId == null) {
        return;
      }

      setOpenDocumentLoading(true);
      const redirectDocumentUrl = `${apiUrl}/${buildDocumentEndpointPath(contentType, documentId, indexId, true)}`;
      window.open(redirectDocumentUrl);
      setOpenDocumentLoading(false);
    },
    [apiUrl, indexId]
  );

  const onPageChange = useCallback((event: GridPageChangeEvent): void => {
    const { skip: nextSkip, take } = event.page;
    if (exceedsMaxSearchSkip(nextSkip)) {
      return;
    }
    setSkip(nextSkip);
    setPageSize(take);
  }, []);

  const onSortChange = useCallback((descriptors: SortDescriptor[]): void => {
    setSort(descriptors.length > 0 ? descriptors : undefined);
    setSkip(0);
  }, []);

  const isLoading =
    isCurrentUserLoading ||
    (!isAccessDenied && (isViewsLoading || isViewLoading || isSearchLoading || openDocumentLoading));

  return {
    isAccessDenied,
    isLoading,
    columns,
    gridData,
    totalCount: data?.totalCount ?? 0,
    skip,
    pageSize,
    sort,
    openDocumentLoading,
    translate,
    onPageChange,
    onSortChange,
    openDocument
  };
};
